<template>
  <div class="effort-page mt-8 mx-8">
    <v-card class="effort-shell pa-4">
      <v-card-title class="effort-title">تنظیمات تقویم تلاش</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field v-model="needle" label="جستجوی دوره" color="black" clearable hide-details />
          </v-col>
          <v-col cols="12" md="8" class="text-left">
            <v-btn color="primary" class="normal-font-btn" @click="getCourses">بروزرسانی</v-btn>
          </v-col>

          <v-col cols="12" md="4">
            <v-card class="course-list-card" outlined>
              <v-list dense>
                <v-list-item
                    v-for="course in filteredCourses"
                    :key="course.id"
                    :class="{ active: selectedCourse && selectedCourse.id === course.id }"
                    @click="selectCourse(course)"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ course.title }}</v-list-item-title>
                    <v-list-item-subtitle>{{ faNumber(course.levels.length) }} سطح تعریف شده</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-icon>
                    <v-icon>mdi-chevron-left</v-icon>
                  </v-list-item-icon>
                </v-list-item>
              </v-list>
            </v-card>
          </v-col>

          <v-col cols="12" md="8">
            <v-card v-if="selectedCourse" class="editor-card" outlined>
              <div class="editor-head">
                <div>
                  <div class="editor-title">{{ selectedCourse.title }}</div>
                  <div class="editor-subtitle">برای هر بازه امتیاز، رنگ نمایش روزهای تقویم را مشخص کنید.</div>
                </div>
                <v-btn color="success" class="normal-font-btn" @click="addLevel">
                  <v-icon left>mdi-plus</v-icon>
                  افزودن سطح
                </v-btn>
              </div>

              <v-row class="target-row">
                <v-col cols="12" md="4">
                  <v-text-field
                      v-model.number="form.weekly_practice_target"
                      type="number"
                      label="هدف تمرین هفتگی"
                      color="black"
                      min="1"
                      max="7"
                      suffix="روز"
                  />
                </v-col>
              </v-row>

              <v-row v-if="!form.levels.length" class="empty-state">
                <v-col cols="12">
                  <v-icon large color="#6B35C8">mdi-calendar-star</v-icon>
                  <div>هنوز سطحی برای این دوره تعریف نشده است.</div>
                </v-col>
              </v-row>

              <v-row v-for="(level, index) in form.levels" :key="index" align="center" class="level-row">
                <v-col cols="12" md="2">
                  <v-text-field v-model.number="level.min_score" type="number" label="از امتیاز" color="black" min="0" />
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field v-model.number="level.max_score" type="number" label="تا امتیاز" color="black" min="0" />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field v-model="level.label" label="عنوان سطح" color="black" />
                </v-col>
                <v-col cols="10" md="4">
                  <v-menu offset-y :close-on-content-click="false">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn class="color-btn normal-font-btn" outlined v-bind="attrs" v-on="on">
                        <span class="color-swatch" :style="{ backgroundColor: level.color }"></span>
                        {{ level.color }}
                      </v-btn>
                    </template>
                    <v-card>
                      <v-color-picker v-model="level.color" mode="hexa" hide-mode-switch dot-size="18" />
                    </v-card>
                  </v-menu>
                </v-col>
                <v-col cols="2" md="1" class="text-left">
                  <v-icon color="error" @click="removeLevel(index)">mdi-trash-can-outline</v-icon>
                </v-col>
              </v-row>

              <div class="preview-title">پیش نمایش</div>
              <div class="preview-row">
                <div class="preview-day empty">۰</div>
                <div
                    v-for="(level, index) in form.levels"
                    :key="'preview-' + index"
                    class="preview-day"
                    :style="{ backgroundColor: level.color, color: readableTextColor(level.color) }"
                >
                  {{ faNumber(level.min_score) }}
                </div>
              </div>

              <div class="actions">
                <v-btn color="success" class="normal-font-btn" :loading="saving" @click="save">ذخیره تنظیمات</v-btn>
              </div>
            </v-card>

            <v-card v-else class="editor-card empty-state" outlined>
              <v-icon large color="#6B35C8">mdi-book-open-page-variant</v-icon>
              <div>یک دوره را برای تنظیم رنگ های تقویم تلاش انتخاب کنید.</div>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "EffortCalendar",
  data() {
    return {
      loading: false,
      saving: false,
      needle: '',
      courses: [],
      selectedCourse: null,
      form: {
        weekly_practice_target: 4,
        levels: []
      }
    }
  },
  computed: {
    filteredCourses() {
      const needle = String(this.needle || '').trim()
      if (!needle) {
        return this.courses
      }

      return this.courses.filter(course => String(course.title || '').includes(needle))
    }
  },
  mounted() {
    this.getCourses()
  },
  methods: {
    getCourses() {
      this.loading = true
      window.axios.get('assessment/effort-calendar/index').then((res) => {
        this.courses = res.data.data || []
        if (!this.selectedCourse && this.courses.length) {
          this.selectCourse(this.courses[0])
        } else if (this.selectedCourse) {
          const fresh = this.courses.find(course => course.id === this.selectedCourse.id)
          if (fresh) {
            this.selectCourse(fresh)
          }
        }
      }).catch((err) => {
        this.openSnackbar(err.response?.data?.message || 'خطا در دریافت دوره ها', '#F1416C')
      }).finally(() => {
        this.loading = false
      })
    },
    selectCourse(course) {
      this.selectedCourse = course
      this.form.weekly_practice_target = Number(course.weekly_practice_target || 4)
      this.form.levels = (course.levels || []).map(level => ({
        min_score: level.min_score,
        max_score: level.max_score,
        color: level.color,
        label: level.label
      }))

      if (!this.form.levels.length) {
        this.form.levels = [
          {min_score: 1, max_score: 4, color: '#D6F5E3', label: 'تلاش کم'},
          {min_score: 5, max_score: 9, color: '#5FE39A', label: 'تلاش مناسب'},
          {min_score: 10, max_score: 1000, color: '#16A34A', label: 'پرتلاش'},
        ]
      }
    },
    addLevel() {
      const last = this.form.levels[this.form.levels.length - 1]
      const start = last ? Number(last.max_score || 0) + 1 : 1
      this.form.levels.push({
        min_score: start,
        max_score: start,
        color: '#6B35C8',
        label: null
      })
    },
    removeLevel(index) {
      this.form.levels.splice(index, 1)
    },
    save() {
      if (!this.selectedCourse) {
        return
      }

      this.saving = true
      window.axios.post('assessment/effort-calendar/update/' + this.selectedCourse.id, this.form).then((res) => {
        this.openSnackbar('تنظیمات تقویم تلاش با موفقیت ذخیره شد.', 'success')
        this.selectedCourse.levels = res.data.data || []
        this.getCourses()
      }).catch((err) => {
        if (err.response?.status === 422) {
          this.showValidationErrors(err, '#F1416C')
        } else {
          this.openSnackbar(err.response?.data?.message || 'خطا در ذخیره تنظیمات', '#F1416C')
        }
      }).finally(() => {
        this.saving = false
      })
    },
    faNumber(value) {
      return String(value ?? 0).replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d])
    },
    readableTextColor(hex) {
      const value = String(hex || '#ffffff').replace('#', '')
      const r = parseInt(value.substring(0, 2), 16)
      const g = parseInt(value.substring(2, 4), 16)
      const b = parseInt(value.substring(4, 6), 16)
      return ((r * 299 + g * 587 + b * 114) / 1000) > 150 ? '#1A0A3C' : '#FFFFFF'
    }
  }
}
</script>

<style scoped>
.effort-page {
  direction: rtl;
}

.effort-shell,
.course-list-card,
.editor-card {
  border-radius: 8px;
}

.effort-title {
  justify-content: flex-end;
  font-size: 16px;
  font-weight: 700;
}

.course-list-card {
  max-height: 640px;
  overflow-y: auto;
}

.v-list-item.active {
  background: #F0E8FF;
  color: #6B35C8;
}

.editor-card {
  padding: 18px;
  min-height: 420px;
}

.editor-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.editor-title {
  font-size: 18px;
  font-weight: 800;
  color: #1A0A3C;
}

.editor-subtitle {
  color: #616171;
  font-size: 13px;
  margin-top: 6px;
}

.level-row {
  border-bottom: 1px solid #EFEAF9;
}

.target-row {
  border-bottom: 1px solid #EFEAF9;
  margin-bottom: 12px;
}

.color-btn {
  width: 100%;
  justify-content: flex-start;
}

.color-swatch {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1px solid #E0E0E0;
  margin-left: 10px;
}

.preview-title {
  margin-top: 22px;
  font-size: 13px;
  font-weight: 800;
  color: #1A0A3C;
}

.preview-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.preview-day {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  border: 1px solid transparent;
}

.preview-day.empty {
  background: #F7F7F7;
  color: #9E9E9E;
  border-color: #E0E0E0;
}

.actions {
  margin-top: 24px;
  text-align: left;
}

.empty-state {
  min-height: 220px;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #616171;
  font-size: 14px;
  font-weight: 700;
}

.normal-font-btn {
  letter-spacing: normal !important;
}
</style>
